import React, { useCallback, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useMount } from 'react-use';

import { SpecCourseLessonViewBanner } from 'common.components/spec-course-lesson-view/__banner/spec-course-lesson-view__banner';
import { SpecCourseLessonViewContentWrapper as CommonSpecCourseLessonViewContentWrapper } from 'common.components/spec-course-lesson-view/__content-wrapper/spec-course-lesson-view__content-wrapper';
import { SpecCourseLessonViewHeader } from 'common.components/spec-course-lesson-view/__header/spec-course-lesson-view__header';
import { SpecCourseLessonViewPassport } from 'common.components/spec-course-lesson-view/__passport/spec-course-lesson-view__passport';
import { SpecCourseLessonView as CommonSpecCourseLessonView } from 'common.components/spec-course-lesson-view/spec-course-lesson-view';
import {
    SPEC_COURSE_LESSON_MODE,
    SPEC_COURSE_LESSON_VIEW_TABS,
} from 'common.components/spec-course-lesson-view/typings';
import { SPEC_COURSE_LESSON_STATUS } from 'common.components/spec-course/typings';
import { scrollTo } from 'utils/scroll-to';

import { specCourseActions } from '../spec-course.store/actions';

import { SpecCourseLessonViewPassportFooter } from './__passport-footer/lesson-view__passport-footer';
import { SpecCourseLessonViewTabContent } from './__tab-content/lesson-view__tab-content';
import { SpecCourseLessonViewTabs } from './__tabs/lesson-view__tabs';

import './lesson-view.scss';

interface Props {
    bannerSrc: string;
    beginDate: Date | null;
    endDate: Date | null;
    isSkeleton: boolean;
    name: string | null;
    status: SPEC_COURSE_LESSON_STATUS;
    mode: SPEC_COURSE_LESSON_MODE;
    tabSlug: string;
}

export const SpecCourseLessonView = (props: Props) => {
    const { bannerSrc, beginDate, endDate, name, status, tabSlug, mode, isSkeleton } = props;

    const dispatch = useDispatch();
    const resultsRef = useRef<HTMLDivElement>(null);
    const awardsRef = useRef<HTMLDivElement>(null);

    const handleResultsButtonClick = useCallback(() => {
        dispatch(specCourseActions.setTab({ tab: SPEC_COURSE_LESSON_VIEW_TABS.RESULTS }));
        scrollToRef(resultsRef);
    }, [dispatch]);

    const handleAwardsButtonClick = useCallback(() => {
        dispatch(specCourseActions.setTab({ tab: SPEC_COURSE_LESSON_VIEW_TABS.RESULTS }));
        scrollToRef(awardsRef);
    }, [dispatch]);

    useMount(() => {
        scrollTo(0, 0);
    });

    const scrollToRef = (ref: React.RefObject<HTMLDivElement>) => {
        setTimeout(() => {
            if (ref.current) {
                window.scrollTo({ top: ref.current.offsetTop, behavior: 'smooth' });
            }
        }, 0);
    };

    return (
        <CommonSpecCourseLessonView mix="teacher-spec-course-lesson-view">
            <SpecCourseLessonViewHeader>
                <SpecCourseLessonViewBanner src={bannerSrc} />
                <SpecCourseLessonViewPassport
                    beginDate={beginDate}
                    endDate={endDate}
                    footer={
                        <SpecCourseLessonViewPassportFooter
                            beginDate={beginDate}
                            mode={mode}
                            onAwardsButtonClick={handleAwardsButtonClick}
                            onResultsButtonClick={handleResultsButtonClick}
                        />
                    }
                    isSkeleton={isSkeleton}
                    name={name}
                    status={status}
                />
            </SpecCourseLessonViewHeader>
            <CommonSpecCourseLessonViewContentWrapper>
                <SpecCourseLessonViewTabs />
                <SpecCourseLessonViewTabContent awardsRef={awardsRef} resultsRef={resultsRef} tabSlug={tabSlug} />
            </CommonSpecCourseLessonViewContentWrapper>
        </CommonSpecCourseLessonView>
    );
};
